import React, { useState } from 'react';
import { Button, Form, Message } from 'semantic';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { joiErrorDetailsToObject } from 'utils/api';
import { Formik } from 'formik';
import SelectField from 'components/form-fields/formik/SelectField';
import InputField from 'components/form-fields/formik/InputField';
import styles from './SandboxForm.module.css';
import ErrorMessage from 'components/ErrorMessage';

interface Sandbox {
  id: string;
  name: string;
  description: string;
  parentSandboxId?: string;
  sandboxData: string;
  sandboxDataSourceId?: string;
  sandboxProductModules: string[];
}

interface Props {
  onSave?(): void;
  onSubmit(sandbox: Partial<Sandbox>): Promise<void>;
  onCancel?(): void;
  initialValues?: Partial<Sandbox>;
  update?: boolean;
}

const SandboxForm: React.FC<Props> = ({
  onSave,
  onCancel,
  onSubmit,
  initialValues,
  update,
}) => {
  const [error, setError] = useState<Error>();

  const { t } = useTranslation();

  const submitHandler = async (
    formValues: Partial<Sandbox>,
    formikBag: any
  ) => {
    try {
      await onSubmit(formValues);
      if (typeof onSave === 'function') {
        onSave?.();
      }
    } catch (error: any) {
      if (Array.isArray(error?.details)) {
        formikBag.setErrors(joiErrorDetailsToObject(error));
      } else {
        formikBag.setStatus(error?.message);
      }
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        onSubmit={submitHandler}
        validationSchema={Yup.object({
          name: Yup.string().required(
            t('sandboxForm.validation.nameRequired', 'Name is required')
          ),
          description: Yup.string().required(
            t(
              'sandboxForm.validation.descriptionRequired',
              'Description is required'
            )
          ),
          sandboxProductModules: Yup.string().required(
            t('sandboxForm.validation.modulesRequired', 'Modules are required')
          ),
        })}
        initialValues={{
          name: '',
          description: '',
          sandboxProductModules: [],
          sandboxData: 'blank',
          sandboxDataSourceId: '',
          parentSandboxId: '',
          ...(initialValues || {}),
        }}>
        {({ status, isSubmitting, handleSubmit, isValid, dirty }) => {
          return (
            <Form id="edit-provider-form">
              {error && <ErrorMessage error={error} />}
              <InputField
                name="name"
                label={t('sandboxForm.name', 'Name')}
                required
              />
              <InputField
                name="description"
                label={t('sandboxForm.description', 'Description')}
                required
              />
              <Form.Field>
                <SelectField
                  required
                  name="sandboxProductModules"
                  label={t('sandboxForm.modules', 'Product modules')}
                  placeholder={t(
                    'sandboxForm.modulesPlaceholder',
                    'Select products to enable for this sandbox'
                  )}
                  options={[
                    {
                      key: 'msp',
                      text: t('sandboxForm.modulesMsp', 'MSP'),
                      value: 'msp',
                    },
                    {
                      key: 'cpo',
                      text: t('sandboxForm.modulesCpo', 'CPO'),
                      value: 'cpo',
                    },
                    {
                      key: 'mspAndCpo',
                      text: t('sandboxForm.modulesMspAndCpo', 'MSP and CPO'),
                      value: 'msp_and_cpo',
                    },
                  ]}
                />
              </Form.Field>
              <SelectField
                name="sandboxData"
                disabled={update}
                label={t('sandboxForm.dataSource', 'Sandbox data')}
                options={[
                  {
                    key: 'blank',
                    text: t('sandboxForm.dataSourceBlank', 'Blank (no data)'),
                    value: 'blank',
                  },
                ]}
              />
              <Form.Field>
                <SelectField
                  name="parentSandboxId"
                  disabled={update}
                  placeholder="None"
                  label={t(
                    'sandboxForm.parentSandbox',
                    'Parent Sandbox (if applicable)'
                  )}
                  options={[
                    {
                      key: 'none',
                      text: t('sandboxForm.noParentSandbox', 'None'),
                      value: '',
                    },
                  ]}
                />
              </Form.Field>
              <div>
                <div>
                  {status && (
                    <Message error>
                      <p>{status}</p>
                    </Message>
                  )}
                </div>
                <div className={styles.modalActions}>
                  <Button as="button" basic onClick={() => onCancel?.()}>
                    {t('sandboxForm.cancel', 'Cancel')}
                  </Button>
                  <Button
                    as="button"
                    loading={isSubmitting}
                    primary
                    disabled={isSubmitting || !isValid || !dirty}
                    onClick={handleSubmit}>
                    {t('sandboxForm.save', 'Save')}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default SandboxForm;
